<template>
  <div :key="`requirement-menu-${requirementId}`" ref="el" class="requirement-menu">
    <item-header type="requirement" @close="$emit('close')">
      <template #content>
        <editable-item-title
          :item="requirement"
          :placeholder="$t('objectNames.requirement')"
        />
      </template>
    </item-header>

    <drawer-block
      v-model:open="drawerStates.requirementGeneral"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-text
          ref="title"
          :label="$t('labels.title')"
          :value="requirement.title"
          @enter="descriptionField?.focus()"
          @update:value="updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :value="requirement.description"
          :label="$t('labels.description')"
          @update:value="updateKey('description', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      v-model:open="drawerStates.requirementConditions"
      :title="$t('objectNames.condition', conditions.length)"
    >
      <div class="column">
        <div>
          <p v-if="conditions.length === 0" class="message-info mv-1" style="">
            {{ $t("texts.noRequirementConditions") }}
          </p>
          <condition-list
            src-type="requirement"
            :src-id="requirement.id"
            :available-activator-types="[
              'target',
              'resource',
              'index',
              'round',
              'roundRange',
              'action',
              'incident'
            ]"
            :selected-condition="selectedCondition"
            @select="select"
            @deleted="deselect"
            @copy="emit('copy', $event)"
          />
        </div>
      </div>
    </drawer-block>
    <menu-group class="mt-auto">
      <button
        class="button button_secondary"
        type="button"
        @click="deleteRequirement"
      >
        {{ $t("actions.deleteRequirement") }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import ConditionList from "../condition/ConditionList.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";

import { useRequirementStore } from "./requirements.store";
import { useConditionStore } from "../condition/conditions.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";

import type { RequirementItem } from "./RequirementItem";
import type { ConditionItem } from "../condition/ConditionItem";

const props = defineProps<{
  requirementId: number;
  selectedCondition?: { type: "condition"; id: number };
  open?: boolean;
}>();

const emit = defineEmits<{
  (event: "select", item: ConditionItem): void;
  (event: "deselect", item: ConditionItem): void;
  (event: "close"): void;
  (event: "copy", item: ConditionItem): void;
}>();

const descriptionField = ref<InstanceType<typeof InputTexteditor>>();

const drawerStates = useDrawerStates();
const requirementStore = useRequirementStore();
const conditionStore = useConditionStore();

const conditions = computed(() => {
  return conditionStore.getBySource({
    type: "requirement",
    id: props.requirementId
  });
});

const requirement = computed(() => {
  return requirementStore.items[props.requirementId];
});

function updateKey<K extends keyof RequirementItem>(
  key: K,
  value: RequirementItem[K]
) {
  requirementStore.updateKey(props.requirementId, key, value);
}

function deleteRequirement() {
  requirementStore.delete({ id: props.requirementId });
}

function select(item: ConditionItem) {
  emit("select", item);
}
function deselect(item: ConditionItem) {
  emit("deselect", item);
}
</script>

<style lang="scss">
.requirement-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  &__line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 4.8rem 1rem;
    gap: 0.25rem;
    align-items: center;
  }
}
</style>
